// File: ReportExpensesAndInvestments.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Grid } from "@aws-amplify/ui-react";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ConfirmModal from "components/utils/ConfirmModal";

ChartJS.register(BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

const sampleData = {
  farms: [
    { id: "farm-1", name: "Finca La Esperanza", active: true },
    { id: "farm-2", name: "Finca El Roble", active: true },
  ],
  expenseTypes: [
    { id: "et-1", name: "Transporte" },
    { id: "et-2", name: "Mano de obra" },
    { id: "et-3", name: "Insumos" },
    { id: "et-4", name: "Infraestructura" },
    { id: "et-5", name: "Consultoría" },
  ],
  // expenseRecords: date, farmId, typeId, description, providerName, value
  expenseRecords: [
    { id: "e1", date: "2025-10-02", farmId: "farm-1", typeId: "et-1", description: "Transporte carga", providerName: "Transportes SA", value: 250000 },
    { id: "e2", date: "2025-10-05", farmId: "farm-1", typeId: "et-3", description: "Compra fertilizante", providerName: "Ferti Ltda", value: 480000 },
    { id: "e3", date: "2025-09-28", farmId: "farm-2", typeId: "et-2", description: "Pago jornales", providerName: "Colaboradores", value: 320000 },
    { id: "e4", date: "2025-10-07", farmId: "farm-1", typeId: "et-4", description: "Reparación bodega", providerName: "Construcciones X", value: 900000 },
    { id: "e5", date: "2025-10-08", farmId: "farm-2", typeId: "et-3", description: "Plaguicida", providerName: "Suministros Agro", value: 150000 },
    { id: "e6", date: "2025-09-20", farmId: "farm-1", typeId: "et-5", description: "Asesoría técnica", providerName: "Consultores Y", value: 600000 },
  ],
};

export default function ReportExpensesAndInvestments(props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = props.data || sampleData || { farms: [], expenseTypes: [], expenseRecords: [] };

  // filtros mínimos: periodo (rango) + finca
  const recordDates = useMemo(() => (data.expenseRecords || []).map((r) => r.date).sort(), [data]);
  const defaultStart = recordDates[0] || moment().format("YYYY-MM-DD");
  const defaultEnd = recordDates[recordDates.length - 1] || moment().format("YYYY-MM-DD");

  const farms = useMemo(() => (data.farms || []).filter((f) => f.active), [data]);
  const [selectedFarmId, setSelectedFarmId] = useState(farms[0]?.id || "");
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  useEffect(() => setSelectedFarmId(farms[0]?.id || ""), [farms]);
  useEffect(() => { setStartDate(defaultStart); setEndDate(defaultEnd); }, [defaultStart, defaultEnd]);

  // filter records by date range and farm
  const filteredRecords = useMemo(() => {
    const s = moment(startDate, "YYYY-MM-DD");
    const e = moment(endDate, "YYYY-MM-DD").endOf("day");
    return (data.expenseRecords || []).filter((r) => {
      const d = moment(r.date, "YYYY-MM-DD");
      if (!d.isBetween(s, e, null, "[]")) return false;
      if (selectedFarmId && r.farmId !== selectedFarmId) return false;
      return true;
    });
  }, [data, startDate, endDate, selectedFarmId]);

  // totals
  const totalValue = useMemo(() => filteredRecords.reduce((acc, r) => acc + (Number(r.value) || 0), 0), [filteredRecords]);
  const recordCount = filteredRecords.length;

  // chart data:
  // Bar: total value by expense type (from filteredRecords)
  const valueByType = useMemo(() => {
    const map = {};
    (filteredRecords || []).forEach((r) => {
      const t = (data.expenseTypes || []).find((et) => et.id === r.typeId)?.name || "Otro";
      map[t] = (map[t] || 0) + Number(r.value || 0);
    });
    return map;
  }, [filteredRecords, data]);

  // Pie: distribution by provider (top providers in filteredRecords)
  const valueByProvider = useMemo(() => {
    const map = {};
    (filteredRecords || []).forEach((r) => {
      const p = r.providerName || "Sin proveedor";
      map[p] = (map[p] || 0) + Number(r.value || 0);
    });
    return map;
  }, [filteredRecords]);

  const typeLabels = useMemo(() => Object.keys(valueByType), [valueByType]);
  const typeValues = useMemo(() => typeLabels.map((k) => valueByType[k]), [typeLabels, valueByType]);

  const providerLabels = useMemo(() => Object.keys(valueByProvider), [valueByProvider]);
  const providerValues = useMemo(() => providerLabels.map((k) => valueByProvider[k]), [providerLabels, valueByProvider]);

  // pastel palette
  const PASTEL_COLORS = [
    "#90d47dff",
    "#f09bb4ff",
    "#fcdc86ff",
    "#8eb4ffff",
    "#ce99f7ff",
    "#ffbe90ff",
    "#98eccdff",
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorsForTypes = useMemo(() => typeLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]), [typeLabels]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorsForProviders = useMemo(() => providerLabels.map((_, i) => PASTEL_COLORS[i % PASTEL_COLORS.length]), [providerLabels]);

  const chartHeightPx = props.chartHeightPx || 360;

  const barData = useMemo(() => ({
    labels: typeLabels,
    datasets: [
      {
        label: "Valor",
        data: typeValues,
        backgroundColor: colorsForTypes,
        borderColor: colorsForTypes,
        borderWidth: 1,
      },
    ],
  }), [typeLabels, typeValues, colorsForTypes]);

  const pieData = useMemo(() => ({
    labels: providerLabels,
    datasets: [
      {
        data: providerValues,
        backgroundColor: colorsForProviders,
        hoverOffset: 8,
      },
    ],
  }), [providerLabels, providerValues, colorsForProviders]);

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false, // width automatic, height by container
    plugins: {
      legend: { position: "bottom" },
      tooltip: { enabled: true },
    },
  };

  const barOptions = {
    ...commonOptions,
    plugins: { ...commonOptions.plugins, legend: { position: "top" }, title: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const pieOptions = {
    ...commonOptions,
    plugins: { ...commonOptions.plugins },
  };

  // modals / confirm
  const [modalDetails, setModalDetails] = useState({ visible: false, itemId: null, itemName: null });
  function handleConfirmDelete() {
    setModalDetails({ visible: false, itemId: null });
  }

  return (
    <div>
      <span className="fs-3">Gastos e Inversiones</span>

      <div className="p-3">
        {/* filtros mínimos: periodo + finca */}
        <Grid className="mb-3" templateColumns={{ medium: "1fr 1fr 1fr" }} rowGap="15px" columnGap="15px">
          <div>
            <label className="form-label">Fecha desde</label>
            <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div>
            <label className="form-label">Fecha hasta</label>
            <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          <div>
            <label className="form-label">Finca</label>
            <select className="form-select" value={selectedFarmId} onChange={(e) => setSelectedFarmId(e.target.value)}>
              {(farms || []).map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
            </select>
          </div>
        </Grid>

        {/* tarjetas mínimas */}
        <CRow className="mb-3">
          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Total Gastos e Inversiones</CCardHeader>
              <CCardBody>
                <div className="fs-4">{totalValue.toLocaleString("es-CO")}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Registros</CCardHeader>
              <CCardBody>
                <div className="fs-4">{recordCount}</div>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={4} sm={12} className="mb-2">
            <CCard>
              <CCardHeader className="small text-muted">Promedio por registro</CCardHeader>
              <CCardBody>
                <div className="fs-4">{(recordCount ? Math.round(totalValue / recordCount) : 0).toLocaleString("es-CO")}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* gráficos: Barras (por tipo) y Torta (por proveedor) */}
        <CRow>
          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">Gastos por Tipo</CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {typeLabels.length === 0 ? (
                  <div className="text-muted">No hay datos para el rango seleccionado.</div>
                ) : (
                  <Bar data={barData} options={barOptions} />
                )}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={6} sm={12} className="mb-3">
            <CCard>
              <CCardHeader className="small text-muted">Distribución por Proveedor</CCardHeader>
              <CCardBody style={{ height: chartHeightPx, width: "100%" }}>
                {providerLabels.length === 0 ? (
                  <div className="text-muted">No hay datos para el rango seleccionado.</div>
                ) : (
                  <Pie data={pieData} options={pieOptions} />
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* tabla con registros */}
        <CRow>
          <CCol>
            <CCard>
              <CCardHeader>Registros de Gastos e Inversiones</CCardHeader>
              <CCardBody>
                <div className="table-responsive">
                  <CTable hover responsive>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>Fecha</CTableHeaderCell>
                        <CTableHeaderCell>Tipo</CTableHeaderCell>
                        <CTableHeaderCell>Descripción</CTableHeaderCell>
                        <CTableHeaderCell>Nombre del Proveedor</CTableHeaderCell>
                        <CTableHeaderCell>Valor</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {filteredRecords.length === 0 ? (
                        <CTableRow>
                          <CTableDataCell colSpan={5} className="text-muted">No hay registros para los filtros seleccionados.</CTableDataCell>
                        </CTableRow>
                      ) : (
                        filteredRecords.map((r) => (
                          <CTableRow key={r.id}>
                            <CTableDataCell>{r.date ?? "-"}</CTableDataCell>
                            <CTableDataCell>{(data.expenseTypes || []).find((et) => et.id === r.typeId)?.name ?? "-"}</CTableDataCell>
                            <CTableDataCell>{r.description ?? "-"}</CTableDataCell>
                            <CTableDataCell>{r.providerName ?? "-"}</CTableDataCell>
                            <CTableDataCell>{(Number(r.value) || 0).toLocaleString("es-CO")}</CTableDataCell>
                          </CTableRow>
                        ))
                      )}
                    </CTableBody>
                  </CTable>
                </div>

                {/* totalización al final */}
                <div className="mt-3 text-end">
                  <strong>Total (tabla): </strong> {totalValue.toLocaleString("es-CO")}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={`¿Estás seguro de eliminar el registro ${modalDetails.itemName}?`}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
        onConfirm={handleConfirmDelete}
      />

      {/* Modal genérico (placeholder) */}
      <CModal size="xl" alignment="center" visible={false} onClose={() => {}} backdrop="static">
        <CModalHeader>
          <CModalTitle>Modal</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          {/* placeholder content */}
        </CModalBody>
      </CModal>
    </div>
  );
}
